import { FC, createElement, HTMLAttributes } from "react";
import clsx from "classnames";
import { useStyles } from "./Typography.style";

export interface ITypographyProps {
  variant?: TypographyVariant;
  color?: TypographyColor;
  inline?: boolean;
  lines?: 0 | 1 | 2 | 3;
  textAlign?: "center" | "left" | "right" | "justify";
  placeholder?: string;
}

export enum TypographyColor {
  Primary = "primary",
  Secondary = "secondary",
  Text1 = "text-1",
  Text2 = "text-2",
  Text3 = "text-3",
  TextError = "text-error",
}

export enum TypographyVariant {
  // Heading h1
  /**
    @property font-size: 48px; 
    @property line-height: 72px;
    @property font-weight: 700;
    @property font-family: Poppins;
  // */
  HeadingH1Bold = "heading-h1-bold",

  // Heading h2
  /**
    @property font-size: 38px;
    @property line-height: 56px;
    @property font-weight: 700;
    @property font-family: Poppins;
  */
  HeadingH2Bold = "heading-h2-bold",

  // Heading h3
  /**
    @property font-size: 32px;
    @property line-height: 48px;
    @property font-weight: 700;
    @property font-family: Poppins;
  */
  HeadingH3Bold = "heading-h3-bold",

  // Heading h4
  /**
    @property font-size: 28px;
    @property line-height: 42px;
    @property font-weight: 700;
    @property font-family: Poppins;
  */
  HeadingH4Bold = "heading-h4-bold",

  // Heading h5
  /**
    @property font-size: 24px;
    @property line-height: 36px;
    @property font-weight: 700;
    @property font-family: Poppins;
  */
  HeadingH5Bold = "heading-h5-bold",

  // Heading h6
  /**
    @property font-size: 20px;
    @property line-height: 30px;
    @property font-weight: 700;
    @property font-family: Poppins;
  */
  HeadingH6Bold = "heading-h6-bold",

  // Heading h7
  /**
    @property font-size: 16px;
    @property line-height: 24px;
    @property font-weight: 700;
    @property font-family: Poppins;
  */
  HeadingH7Bold = "heading-h7-bold",

  // Heading h8
  /**
    @property font-size: 14px;
    @property line-height: 20px;
    @property font-weight: 700;
    @property font-family: Poppins;
  */
  HeadingH8Bold = "heading-h8-bold",

  // Body b1
  /**
    @property font-size: 24px; 
    @property line-height: 36px;
    @property font-weight: 600;
    @property font-family: Poppins;
  */
  BodyB1Bold = "body-b1-bold",
  /**
    @property font-size: 24px; 
    @property line-height: 36px;
    @property font-weight: 400;
    @property font-family: Poppins;
  */
  BodyB1Regular = "body-b1-regular",
  /**
    @property font-size: 24px; 
    @property line-height: 36px;
    @property font-weight: 300;
    @property font-family: Poppins;
  */
  BodyB1Medium = "body-b1-light",

  // Body b2
  /**
    @property font-size: 20px; 
    @property line-height: 30px;
    @property font-weight: 600;
    @property font-family: Poppins;
  */
  BodyB2Bold = "body-b2-bold",
  /**
    @property font-size: 20px; 
    @property line-height: 30px;
    @property font-weight: 400;
    @property font-family: Poppins;
  */
  BodyB2Regular = "body-b2-regular",
  /**
    @property font-size: 20px; 
    @property line-height: 30px;
    @property font-weight: 300;
    @property font-family: Poppins;
  */
  BodyB2Medium = "body-b2-light",

  // Body b3
  /**
    @property font-size: 18px; 
    @property line-height: 26px;
    @property font-weight: 600;
    @property font-family: Poppins;
  */
  BodyB3Bold = "body-b3-bold",
  /**
    @property font-size: 18px; 
    @property line-height: 26px;
    @property font-weight: 400;
    @property font-family: Poppins;
  */
  BodyB3Regular = "body-b3-regular",
  /**
    @property font-size: 18px; 
    @property line-height: 26px;
    @property font-weight: 300;
    @property font-family: Poppins;
  */
  BodyB3Medium = "body-b3-light",

  // Body b4
  /**
    @property font-size: 16px; 
    @property line-height: 24px;
    @property font-weight: 600;
    @property font-family: Poppins;
  */
  BodyB4Bold = "body-b4-bold",
  /**
    @property font-size: 16px; 
    @property line-height: 24px;
    @property font-weight: 400;
    @property font-family: Poppins;
  */
  BodyB4Regular = "body-b4-regular",
  /**
    @property font-size: 16px; 
    @property line-height: 24px;
    @property font-weight: 300;
    @property font-family: Poppins;
  */
  BodyB4Medium = "body-b4-light",

  // Body b5
  /**
    @property font-size: 14px; 
    @property line-height: 20px;
    @property font-weight: 600;
    @property font-family: Poppins;
  */
  BodyB5Bold = "body-b5-bold",
  /**
    @property font-size: 14px; 
    @property line-height: 20px;
    @property font-weight: 400;
    @property font-family: Poppins;
  */
  BodyB5Regular = "body-b5-regular",
  /**
    @property font-size: 14px; 
    @property line-height: 20px;
    @property font-weight: 300;    
    @property font-family: Poppins;
  */
  BodyB5Medium = "body-b5-light",

  // Body b6
  /**
    @property font-size: 12px; 
    @property line-height: 18px;
    @property font-weight: 600;
    @property font-family: Poppins;
  */
  BodyB6Bold = "body-b6-bold",
  /**
    @property font-size: 12px; 
    @property line-height: 18px;
    @property font-weight: 400;
    @property font-family: Poppins;
  */
  BodyB6Regular = "body-b6-regular",
  /**
    @property font-size: 12px; 
    @property line-height: 18px;
    @property font-weight: 300;    
    @property font-family: Poppins;
  */
  BodyB6Medium = "body-b6-light",
}

const typographyMapping: { [key in TypographyVariant]: string } = {
  // Heading
  "heading-h1-bold": "h1",
  "heading-h2-bold": "h2",
  "heading-h3-bold": "h3",
  "heading-h4-bold": "h4",
  "heading-h5-bold": "h5",
  "heading-h6-bold": "h6",
  "heading-h7-bold": "p",
  "heading-h8-bold": "p",

  // Body b1
  "body-b1-bold": "p",
  "body-b1-regular": "p",
  "body-b1-light": "p",

  // Body b2
  "body-b2-bold": "p",
  "body-b2-regular": "p",
  "body-b2-light": "p",

  // Body b3
  "body-b3-bold": "p",
  "body-b3-regular": "p",
  "body-b3-light": "p",

  // Body b4
  "body-b4-bold": "p",
  "body-b4-regular": "p",
  "body-b4-light": "p",

  // Body b5
  "body-b5-bold": "p",
  "body-b5-regular": "p",
  "body-b5-light": "p",

  // Body b6
  "body-b6-bold": "p",
  "body-b6-regular": "p",
  "body-b6-light": "p",
};

export const Typography: FC<
  ITypographyProps & HTMLAttributes<HTMLParagraphElement | HTMLTitleElement>
> = ({
  children,
  textAlign,
  variant = TypographyVariant.BodyB1Regular,
  color,
  inline = false,
  lines,
  style,
  ...otherProps
}) => {
  const classes = useStyles();
  return createElement(
    inline
      ? "span"
      : typographyMapping[variant || TypographyVariant.BodyB1Regular],
    {
      ...otherProps,
      style: {
        ...(textAlign ? { textAlign } : undefined),
        display: inline ? "inline-block" : undefined,
        ...style,
      },
      className: clsx(classes.root, variant, color, otherProps.className, {
        [`lines-${lines}`]: !!lines,
      }),
    },
    children,
  );
};
